"use client";

import { useEffect, useRef, useState } from "react";
import { RiArrowRightUpLine, RiBookOpenLine } from "react-icons/ri";

import { ProgressBarLink } from "@/components/progress-bar";
import AIBadge from "@/components/ui/ai-badge";
import Badge from "@/components/ui/badge";
import { CheckCircle } from "@/components/ui/check-circle";

import { useCanUseAskDecoda } from "@/hooks/use-can-use-ask-decoda";

import { requestAnchorHighlight } from "@/lib/settings-anchor-highlight";
import {
  type SetupSurface,
  captureSetupItemAction,
} from "@/lib/setup-checklist-analytics";
import { cx, tv } from "@/lib/tailwind-utils";

import type { SetupItemDetail } from "@/types/setup-checklist";

import {
  useCompleteSetupItem,
  useSkipSetupItem,
  useUncompleteSetupItem,
  useUnskipSetupItem,
} from "@/api/provider/setup-checklist/setup-checklist-query";
import {
  askDecodaSidebarDefinition,
  assistantSidebarDefinition,
} from "@/features/sidebar/assistant-sidebar";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";

interface SetupItemRowProps {
  ref?: React.Ref<HTMLLIElement>;
  item: SetupItemDetail;
  /** Open prerequisites in the same group. */
  afterTitles?: string[];
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Called once the completion animation ends. */
  onSettled: () => void;
  /** Called when Open is clicked, alongside the navigation. */
  onOpen?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** `md` is the roomier layout the full page uses. */
  size?: "sm" | "md";
  surface: SetupSurface;
}

const setupItemRowVariants = tv({
  slots: {
    header: "flex items-start gap-2",
    check: "flex shrink-0 items-center",
    titleLine: "flex gap-1.5",
    title: "min-w-0",
    details: "space-y-2.5 pb-3",
    description: "max-w-prose leading-relaxed text-foreground-secondary",
    actions: "flex min-w-0 flex-wrap items-center gap-y-1.5",
    open: "inline-flex items-center gap-1 rounded border border-border bg-background font-medium text-foreground hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    textLink:
      "inline-flex items-center gap-1 rounded-sm font-medium text-foreground underline-offset-2 hover:underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    skip: "shrink-0 rounded-sm text-foreground-muted underline-offset-2 hover:underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    icon: "",
  },
  variants: {
    size: {
      sm: {
        header: "py-2",
        check: "h-4",
        titleLine: "items-start",
        title: "flex-1 text-caption-strong",
        details: "pl-6",
        description: "text-caption",
        actions: "flex-1 gap-x-3",
        open: "px-2 py-1 text-caption",
        textLink: "text-caption",
        skip: "text-caption",
        icon: "size-3",
      },
      md: {
        header: "py-2.5",
        check: "h-5",
        titleLine: "flex-wrap items-center gap-x-2",
        title: "text-body-strong",
        details: "pl-7",
        description: "text-body",
        actions: "gap-x-5",
        open: "px-3 py-1.5 text-body",
        textLink: "text-body",
        skip: "text-body",
        icon: "size-4",
      },
    },
    settled: {
      true: { header: "py-1" },
    },
  },
  defaultVariants: {
    size: "sm",
    settled: false,
  },
});

export function SetupItemRow({
  ref,
  item,
  afterTitles = [],
  expanded,
  onToggleExpanded,
  onSettled,
  onOpen,
  size = "sm",
  surface,
}: SetupItemRowProps) {
  const { definition: def } = item;

  const complete = useCompleteSetupItem();
  const uncomplete = useUncompleteSetupItem();
  const skip = useSkipSetupItem();
  const unskip = useUnskipSetupItem();

  const isDone = item.status === "DONE";
  const isSkipped = item.status === "SKIPPED";
  const isSettled = isDone || isSkipped;
  const busy =
    complete.isPending ||
    uncomplete.isPending ||
    skip.isPending ||
    unskip.isPending;

  const canToggle = !busy && (!isDone || item.completedVia === "MANUAL");

  const [celebrating, setCelebrating] = useState(false);
  const celebrationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (celebrationTimer.current) clearTimeout(celebrationTimer.current);
    },
    [],
  );

  const toggleDone = () => {
    if (isDone) {
      uncomplete.mutate({ itemId: item.id });
      captureSetupItemAction(item, "uncomplete", surface);
      return;
    }
    // Mutate before the timer, or an unmount or double click can lose or repeat it.
    setCelebrating(true);
    complete.mutate({ itemId: item.id });
    captureSetupItemAction(item, "complete", surface);
    celebrationTimer.current = setTimeout(() => {
      setCelebrating(false);
      onSettled();
    }, 350);
  };

  const canUseAskDecoda = useCanUseAskDecoda();

  const askDecoda = () => {
    captureSetupItemAction(item, "ask_decoda", surface);
    const store = useAskDecodaStore.getState();
    store.startNewConversation();
    store.setSetupItem(item.id);
    store.setInitialQuestion(`Help me with this setup task: ${def.title}`);
    useRightSidebarStore.getState().open(assistantSidebarDefinition, {
      targetId: askDecodaSidebarDefinition.id,
    });
  };

  const styles = setupItemRowVariants({ size, settled: isSettled });

  const skipButton = (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        skip.mutate({ itemId: item.id });
        captureSetupItemAction(item, "skip", surface);
        onSettled();
      }}
      className={styles.skip()}
    >
      Skip
    </button>
  );

  return (
    <li
      ref={ref}
      className={cx(
        "-mx-2 rounded-md px-2 transition-colors duration-150 motion-reduce:transition-none",
        expanded ? "pb-1" : "hover:bg-hover",
        isSettled && "opacity-60",
      )}
    >
      <div className={styles.header()}>
        <span className={styles.check()}>
          <CheckCircle
            checked={isDone}
            celebrating={celebrating}
            onToggle={canToggle ? toggleDone : undefined}
            disabled={busy}
            label={isDone ? `Reopen ${def.title}` : `Mark ${def.title} done`}
          />
        </span>

        <button
          type="button"
          onClick={() => {
            if (!expanded) captureSetupItemAction(item, "expand", surface);
            onToggleExpanded();
          }}
          aria-expanded={expanded}
          className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className={styles.titleLine()}>
            <span
              className={styles.title({
                className: isDone
                  ? "text-foreground-muted line-through"
                  : "text-foreground",
              })}
            >
              {def.title}
            </span>
            {def.importance === "MANDATORY" &&
              !def.critical &&
              item.status === "OPEN" && (
                <span className="mt-0.5 shrink-0 text-tiny font-medium uppercase tracking-wide text-foreground-muted">
                  Required
                </span>
              )}
            {def.critical && item.status === "OPEN" && (
              <Badge variant="critical" size="xs" className="mt-px shrink-0">
                Critical
              </Badge>
            )}
            {isSkipped && (
              <Badge size="xs" className="mt-px shrink-0">
                Skipped
              </Badge>
            )}
          </span>
        </button>
      </div>

      <div
        className={cx(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div
          className="min-h-0 overflow-hidden"
          inert={!expanded}
          aria-hidden={!expanded}
        >
          <div className={styles.details()}>
            <p className={styles.description()}>{def.description}</p>

            {afterTitles.length > 0 && item.status === "OPEN" && (
              <p className="text-tiny text-foreground-muted">
                {`After: ${afterTitles.join(", ")}`}
              </p>
            )}

            {isSkipped ? (
              <div className="space-y-1.5">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    unskip.mutate({ itemId: item.id });
                    captureSetupItemAction(item, "unskip", surface);
                  }}
                  className={styles.textLink()}
                >
                  Unskip
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className={styles.actions()}>
                  {def.route !== null && (
                    <ProgressBarLink
                      href={def.route}
                      newWindow={def.opensNewTab}
                      onClick={(event) => {
                        captureSetupItemAction(item, "open", surface);
                        requestAnchorHighlight(def.route ?? "");
                        onOpen?.(event);
                      }}
                      className={styles.open()}
                    >
                      <span>Open</span>
                      <RiArrowRightUpLine
                        className={styles.icon()}
                        aria-hidden="true"
                      />
                    </ProgressBarLink>
                  )}

                  {def.agentEligible && canUseAskDecoda && (
                    <button
                      type="button"
                      onClick={askDecoda}
                      className={styles.textLink()}
                    >
                      <AIBadge
                        size={size === "md" ? 14 : 12}
                        className="mr-0"
                      />
                      <span>Ask Decoda</span>
                    </button>
                  )}

                  {def.docsUrl && (
                    <a
                      href={def.docsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        captureSetupItemAction(item, "docs", surface)
                      }
                      className={styles.textLink()}
                    >
                      <RiBookOpenLine
                        className={styles.icon()}
                        aria-hidden="true"
                      />
                      <span>Guide</span>
                    </a>
                  )}

                  {!isDone && size === "md" && skipButton}
                </div>

                {!isDone && size === "sm" && skipButton}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
